/**
 * ThermalTicketSimulator — Simula la tira de papel térmico 58mm/80mm en pantalla.
 *
 * La tira ocupa exactamente `cols` caracteres monoespaciados (48 @ 80mm, 32 @ 58mm):
 * ancho = `${cols}ch` con la misma fuente del contenido, sin margen derecho vacío.
 *
 * QR opcional: si llega `qrUrl`, se dibuja un QR real al final de la tira
 * (como lo imprime la térmica con GS ( k), junto con `qrLabel` si existe.
 * Así la Vista Térmica Real coincide 1:1 con lo que sale en papel.
 */

import { forwardRef, useCallback, useMemo, useRef } from "react";
import type { CSSProperties } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  getTicketMaxWidth,
  THERMAL_SIM_FONT_PX,
  THERMAL_SIM_LINE_HEIGHT,
  THERMAL_SIM_LINE_SPACING,
  type ThermalPreviewLine,
} from "@/features/caja/utils/escposLayout";
import { plainTextLinesToPreviewLines } from "@/features/caja/utils/escPosPlainTextDecode";
import { TicketTornPaperShell } from "@/features/caja/components/TicketTornPaperShell";

const FONT_FAMILY = "'Courier New', Courier, monospace";

export interface ThermalTicketSimulatorProps {
  lines: string[];
  paperWidthMm: 58 | 80;
  /** Líneas con alineación explícita (cierre/movimiento desde decode ESC/POS). */
  previewLines?: ThermalPreviewLine[];
  /** URL del QR del pie (opcional; solo se dibuja si existe). */
  qrUrl?: string;
  /** Texto sobre el QR (opcional). */
  qrLabel?: string;
  /** Familia tipográfica de la tira (por defecto monoespaciada). */
  fontFamily?: string;
  /** Multiplicador del tamaño de fuente (1 = base del papel). */
  fontScale?: number;
}

function lineStyleForKind(
  ln: ThermalPreviewLine,
  fontPx: number,
): CSSProperties {
  const spacing = THERMAL_SIM_LINE_SPACING;
  const base: CSSProperties = {
    display: "block",
    width: "100%",
    textAlign: ln.align,
    whiteSpace: "pre",
    overflow: "hidden",
    margin: 0,
    padding: 0,
    lineHeight: THERMAL_SIM_LINE_HEIGHT,
    fontWeight: ln.bold ? 700 : 400,
    color: ln.bold ? "#000000" : "#1a1a1a",
  };

  switch (ln.kind) {
    case "blank":
      return {
        ...base,
        minHeight: `${Math.round(fontPx * spacing.blankRatio)}px`,
      };
    case "divider":
      return {
        ...base,
        marginTop: spacing.dividerMyPx,
        marginBottom: spacing.dividerMyPx,
      };
    case "totalArticulo":
      return { ...base, marginBottom: spacing.totalArticuloMbPx };
    case "tableHeader":
      return { ...base, marginBottom: spacing.tableHeaderMbPx };
    default:
      return base;
  }
}

export const ThermalTicketSimulator = forwardRef<
  HTMLDivElement,
  ThermalTicketSimulatorProps
>(function ThermalTicketSimulator(
  { lines, paperWidthMm, previewLines, qrUrl, qrLabel, fontFamily, fontScale },
  forwardedRef,
) {
  const cols = getTicketMaxWidth(paperWidthMm);
  const fontPx = Math.max(
    6,
    Math.round(THERMAL_SIM_FONT_PX[paperWidthMm] * (fontScale ?? 1)),
  );
  const resolvedFontFamily = fontFamily ?? FONT_FAMILY;
  const stripRef = useRef<HTMLDivElement | null>(null);

  const setStripRef = useCallback(
    (node: HTMLDivElement | null) => {
      stripRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const displayLines = useMemo(() => {
    if (previewLines?.length) return previewLines;
    return plainTextLinesToPreviewLines(lines, cols);
  }, [previewLines, lines, cols]);

  /** El QR se dibuja solo cuando existe URL (mismo criterio que la impresión). */
  const qrSize = paperWidthMm === 80 ? 88 : 72;

  /** `ch` se calcula con esta fuente/tamaño → ancho físico = N columnas exactas. */
  const stripStyle: CSSProperties = {
    width: `${cols}ch`,
    minWidth: `${cols}ch`,
    maxWidth: `${cols}ch`,
    fontFamily: resolvedFontFamily,
    fontSize: `${fontPx}px`,
    flexShrink: 0,
    boxSizing: "border-box",
  };

  const paperStyle: CSSProperties = {
    width: "100%",
    backgroundColor: "#fcfcfc",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  const contentStyle: CSSProperties = {
    width: "100%",
    fontFamily: resolvedFontFamily,
    fontSize: `${fontPx}px`,
    lineHeight: THERMAL_SIM_LINE_HEIGHT,
    letterSpacing: 0,
    color: "#111111",
    padding: "10px 0",
    boxSizing: "border-box",
  };

  return (
    <div className="ticket-print-area thermal-ticket-root flex w-full min-w-0 justify-center">
      <div
        ref={setStripRef}
        className="thermal-strip-measure shrink-0"
        style={stripStyle}
      >
        <TicketTornPaperShell
          className={`thermal-paper-strip thermal-paper-${paperWidthMm}mm w-full`}
        >
          <div
            data-thermal-export-root
            data-paper-width-mm={paperWidthMm}
            data-thermal-font-px={fontPx}
            data-capture-scale="3"
            style={paperStyle}
          >
            <div className="thermal-sim-container" style={contentStyle}>
              {displayLines.map((ln, i) => (
                <div key={i} style={lineStyleForKind(ln, fontPx)}>
                  {ln.text || "\u00A0"}
                </div>
              ))}
              {qrUrl && (
                <div className="pt-2 flex flex-col items-center gap-1">
                  {qrLabel && (
                    <span
                      style={{
                        fontSize: `${Math.max(7, fontPx - 1)}px`,
                        lineHeight: 1.15,
                        textAlign: "center",
                        whiteSpace: "pre-wrap",
                        color: "#111111",
                      }}
                    >
                      {qrLabel}
                    </span>
                  )}
                  <QRCodeCanvas
                    value={qrUrl}
                    size={qrSize}
                    level="M"
                    marginSize={1}
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
              )}
            </div>
          </div>
        </TicketTornPaperShell>
      </div>
    </div>
  );
});
